// ─── Settings Route ───
// The PERSONAL settings surface (ADR 0035: "Tú"). Six ungated tabs, all
// about the signed-in person: General (Appearance + About) · Perfil ·
// Mis conocimientos · Notificaciones · Mi acceso (the personal half of the
// D7 Access split) · Solicitudes (the whole hub, D6 — approvals are still
// "things involving me"). Every system-administration surface lives on
// /app/admin (routes/app/admin.tsx) behind its own gated entries.
//
// `?tab=` — deep-linkable (e.g. `OwnDayPopover`'s "View in My requests" →
// `?tab=requests`; notification emails). Absent/invalid falls back to
// 'general'. ⚠️ The schema below also keeps EVERY id that moved to
// /app/admin: those hit the permanent redirect in SettingsPage — old links,
// including ones in already-sent emails, must never silently lose their
// destination (ADR 0035 D10).

import { APP_NAME } from '@a8-planner/shared/constants/app';
import { createFileRoute, Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import {
  Sun,
  Moon,
  Monitor,
  User,
  Settings,
  Lock,
  Mail,
  InboxIcon,
  ClipboardCheck,
  Layers,
  Square,
} from 'lucide-react';
import { useUIStore, type Theme, type Transparency } from '../../stores/ui.store';
import { useTranslation } from 'react-i18next';
import { PersonCircle } from '../../components/PersonCircle';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  visibleNavDestinations,
  isEnabledInSimpleMode,
} from '../../features/layout/navDestinations';
import type { UpdateSurfacePreferencesInput } from '@a8-planner/shared';
import { meApi } from '../../lib/api/me';
import { applyUserLanguage } from '../../lib/i18n';
import { useAuth } from '../../features/auth/AuthProvider';
import { NotificationsTab } from '../../features/settings/NotificationsTab';
import { MyAccessView } from '../../features/access/MyAccessView';
import { RequestsHub } from '../../features/requests/RequestsHub';
import { MyKnowledgeTab } from '../../features/knowledge/MyKnowledgeTab';
import { Section } from '../../features/settings/Section';

const settingsSearchSchema = z.object({
  tab: z
    .enum([
      'general',
      'profile',
      'my-knowledge',
      'notifications',
      'access',
      'clients',
      'dev-machines',
      'alerts',
      'requests',
      'companies',
      'calendars',
      'knowledge',
      'admin',
      'deleted',
      'task-types',
    ])
    .optional()
    .catch(undefined),
  /** Deep-link into a machine's detail panel (ADR 0025): ?tab=dev-machines&machineId=… */
  machineId: z.string().uuid().optional().catch(undefined),
  /**
   * Knowledge tab's questionnaire status filter (GHE #448 — previously owned directly via the
   * History API by features/knowledge/useQuestionnaireListFilterParam.ts).
   *
   * Absent means "all"; the hook never writes the literal string "all", so the URL stays clean
   * for the default. These values are never JSON-parseable, so TanStack's parser hands them over
   * as plain strings and re-encodes them unquoted — no #445 treatment needed.
   */
  questionnaireStatus: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']).optional().catch(undefined),
  /**
   * Clients tab's debounced search text (GHE #448 — same history there).
   *
   * ⚠️ This is #445 in reverse, and the asymmetry is deliberate. Free text must stay a STRING:
   * someone searching `8` has to keep meaning the text "8", so TanStack quoting it in the address
   * bar (`?clientSearch="8"`) is the round-trip working, not a defect — dropping the quotes would
   * make it parse back as the number 8. What the union guards is the other direction: a
   * hand-typed or truncated `?clientSearch=8` arrives as a NUMBER, and a bare `z.string()` would
   * reject it and — via `.catch(undefined)` — silently drop the search the user can see in their
   * own URL.
   */
  clientSearch: z
    .union([z.string(), z.number(), z.boolean()])
    .optional()
    .catch(undefined)
    .transform((v): string | undefined => (v === undefined ? undefined : String(v))),
});

export const Route = createFileRoute('/app/settings')({
  validateSearch: settingsSearchSchema,
  component: SettingsPage,
});

// ─── Tab definitions ───

type SettingsTab =
  | 'general'
  | 'profile'
  | 'my-knowledge'
  | 'notifications'
  | 'access'
  | 'clients'
  | 'dev-machines'
  | 'alerts'
  | 'requests'
  | 'companies'
  | 'calendars'
  | 'knowledge'
  | 'admin'
  | 'deleted'
  | 'task-types';

interface TabItemShape {
  id: SettingsTab;
  labelKey: string;
  icon: typeof Settings;
  // The accessOnly/adminOnly/clientsOnly/alertsOnly gate flags retired with
  // ADR 0035 phase 4: every remaining tab is personal and visible to every
  // authenticated user — role-gated surfaces all live on /app/admin now.
}

// `as const satisfies` keeps each `labelKey` as a literal string (so it stays
// a valid, compile-time-checked t() key) while still validating the overall
// shape against TabItemShape.
const TABS = [
  {
    id: 'general',
    labelKey: 'tabs.general',
    icon: Settings,
  },
  {
    id: 'profile',
    labelKey: 'tabs.profile',
    icon: User,
  },
  {
    id: 'my-knowledge',
    labelKey: 'tabs.myKnowledge',
    icon: ClipboardCheck,
  },
  {
    id: 'notifications',
    labelKey: 'tabs.notifications',
    icon: Mail,
  },
  {
    id: 'access',
    // Renamed by the D7 split: this tab is the personal "Mi acceso" half; the
    // console keeps the plain "Acceso" label on /app/admin.
    labelKey: 'tabs.myAccess',
    icon: Lock,
  },
  {
    id: 'requests',
    labelKey: 'tabs.requests',
    icon: InboxIcon,
  },
  // Empresas · Calendarios · Conocimientos (builder) · Administración ·
  // Eliminados (phase 1) and Clientes · Tipos de tarea · Alertas (phase 2)
  // moved to the /app/admin console (ADR 0035). Their ids stay in
  // `settingsSearchSchema` so old `?tab=` links — including the ones living
  // in already-sent emails — hit the permanent redirect below instead of
  // being silently dropped.
] as const satisfies readonly TabItemShape[];

// ─── Theme option type ───

interface ThemeOptionShape {
  value: Theme;
  labelKey: string;
  descriptionKey: string;
  icon: typeof Sun;
}

const THEME_OPTIONS = [
  {
    value: 'light',
    labelKey: 'appearance.light.label',
    descriptionKey: 'appearance.light.description',
    icon: Sun,
  },
  {
    value: 'dark',
    labelKey: 'appearance.dark.label',
    descriptionKey: 'appearance.dark.description',
    icon: Moon,
  },
  {
    value: 'system',
    labelKey: 'appearance.system.label',
    descriptionKey: 'appearance.system.description',
    icon: Monitor,
  },
] as const satisfies readonly ThemeOptionShape[];

// ─── Section wrapper ───

// `Section` moved to features/settings/Section.tsx (ADR 0035 phase 1) — the
// admin console renders the same titled-section chrome.

// ─── Appearance section ───

function AppearanceSection(): React.ReactElement {
  const { t } = useTranslation('settings');
  const { theme, setTheme } = useUIStore();
  const { isAuthenticated } = useAuth();

  // Batch B (#47): theme is server-stored so it follows the person across
  // devices. Local apply stays instant; the PATCH is best-effort write-through
  // (a failure keeps the local choice — next login re-syncs from the server).
  const persistTheme = (value: Theme): void => {
    setTheme(value);
    if (isAuthenticated) {
      void meApi.updateProfile({ theme: value }).catch(() => undefined);
    }
  };

  return (
    <Section
      title={t('appearance.title')}
      description={t('appearance.description', { appName: APP_NAME })}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {THEME_OPTIONS.map(({ value, labelKey, descriptionKey, icon: Icon }) => {
          const isActive = theme === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => persistTheme(value)}
              aria-pressed={isActive}
              className={`flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
                isActive
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                  : 'border-border bg-surface-raised hover:border-border-strong hover:bg-surface-overlay'
              }`}
            >
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-md ${
                  isActive
                    ? 'bg-primary-100 text-primary-600 dark:bg-primary-50'
                    : 'bg-surface-overlay text-[var(--text-secondary)]'
                }`}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <p
                  className={`text-sm font-medium ${
                    isActive ? 'text-primary-700' : 'text-[var(--text-primary)]'
                  }`}
                >
                  {t(labelKey)}
                </p>
                <p className="mt-0.5 text-xs text-[var(--text-secondary)]">{t(descriptionKey)}</p>
              </div>
            </button>
          );
        })}
      </div>
    </Section>
  );
}

// ─── Transparency section (GHE #1279) ───
//
// Va justo debajo de Apariencia y con SUS mismas tarjetas, no con una forma nueva: es la misma
// clase de decisión (elegir uno de tres) y el propietario fue explícito — «tiene que ser 1 app con
// un estilo».
//
// 🚨 Por qué existe, que no es obvio: `prefers-reduced-transparency` **no la implementa Safari**
// (bug de WebKit abierto desde 2017) y Firefox la trae apagada por huella digital. macOS inventó el
// ajuste «Reducir transparencia» y su propio navegador no lo expone, así que la persona con más
// motivos para quererlo apagado es justo la que no podemos detectar. Cobertura real ~75%, y el 25%
// que falta no está repartido al azar.
//
// Los tres estados son los del tema, y por el mismo motivo: `on` no es redundante con no haber
// elegido nada — distingue «nunca lo he tocado» de «lo quiero encendido aunque mi sistema pida lo
// contrario». Sin esa distinción, la preferencia del sistema dejaría de servir para nada.

const TRANSPARENCY_OPTIONS = [
  {
    value: 'system',
    labelKey: 'transparency.system.label',
    descriptionKey: 'transparency.system.description',
    icon: Monitor,
  },
  {
    value: 'on',
    labelKey: 'transparency.on.label',
    descriptionKey: 'transparency.on.description',
    icon: Layers,
  },
  {
    value: 'off',
    labelKey: 'transparency.off.label',
    descriptionKey: 'transparency.off.description',
    icon: Square,
  },
] as const satisfies readonly {
  value: Transparency;
  labelKey: string;
  descriptionKey: string;
  icon: typeof Sun;
}[];

function TransparencySection(): React.ReactElement {
  const { t } = useTranslation('settings');
  const { transparency, setTransparency } = useUIStore();
  const { isAuthenticated } = useAuth();

  // Igual que el tema: aplicación local instantánea y PATCH best-effort. Si el PATCH falla, la
  // elección local se queda y el siguiente login la re-sincroniza desde el servidor.
  const persist = (value: Transparency): void => {
    setTransparency(value);
    if (isAuthenticated) {
      void meApi.updateProfile({ transparency: value }).catch(() => undefined);
    }
  };

  // El aviso sólo aparece donde el navegador SÍ detecta la preferencia — y desaparece solo en
  // Safari, que es exactamente donde sería mentira. `matchMedia` de una consulta desconocida
  // devuelve `matches: false`, así que esto degrada bien sin necesidad de detectar el navegador.
  const sistemaLaPide =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-transparency: reduce)').matches;

  return (
    <Section title={t('transparency.title')} description={t('transparency.description')}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {TRANSPARENCY_OPTIONS.map(({ value, labelKey, descriptionKey, icon: Icon }) => {
          const isActive = transparency === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => persist(value)}
              aria-pressed={isActive}
              className={`flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
                isActive
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                  : 'border-border bg-surface-raised hover:border-border-strong hover:bg-surface-overlay'
              }`}
            >
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-md ${
                  isActive
                    ? 'bg-primary-100 text-primary-600 dark:bg-primary-50'
                    : 'bg-surface-overlay text-[var(--text-secondary)]'
                }`}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <p
                  className={`text-sm font-medium ${
                    isActive ? 'text-primary-700' : 'text-[var(--text-primary)]'
                  }`}
                >
                  {t(labelKey)}
                </p>
                <p className="mt-0.5 text-xs text-[var(--text-secondary)]">{t(descriptionKey)}</p>
              </div>
            </button>
          );
        })}
      </div>
      {sistemaLaPide && (
        <p className="mt-3 rounded-md bg-surface-overlay px-3 py-2 text-xs text-[var(--text-secondary)]">
          {t('transparency.systemDetected')}
        </p>
      )}
    </Section>
  );
}

// ─── Language section (Batch B, #47) ───

const LANGUAGE_OPTIONS = [
  { value: 'es', labelKey: 'common:language.es' },
  { value: 'en', labelKey: 'common:language.en' },
] as const;

function LanguageSection(): React.ReactElement {
  const { t } = useTranslation(['settings', 'common']);
  const { user, setUser } = useAuth();

  const mutation = useMutation({
    mutationFn: (language: 'es' | 'en') => meApi.updateProfile({ language }),
    onSuccess: (response) => {
      // The PATCH echoes the fresh AuthenticatedUser — keep the auth context
      // in sync and switch the live UI language (+ a8planner_lang mirror for the
      // pre-login screen on this device).
      setUser(response.data.user);
      applyUserLanguage(response.data.user.language);
    },
    onError: () => {
      toast.error(t('common:state.error'));
    },
  });

  const active = user?.language ?? 'es';

  return (
    <Section title={t('common:language.label')} description={t('common:language.description')}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {LANGUAGE_OPTIONS.map(({ value, labelKey }) => {
          const isActive = active === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => mutation.mutate(value)}
              disabled={mutation.isPending}
              aria-pressed={isActive}
              className={`flex items-center gap-3 rounded-lg border p-4 text-left transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] disabled:opacity-60 ${
                isActive
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                  : 'border-border bg-surface-raised hover:border-border-strong hover:bg-surface-overlay'
              }`}
            >
              <p
                className={`text-sm font-medium ${
                  isActive ? 'text-primary-700' : 'text-[var(--text-primary)]'
                }`}
              >
                {t(labelKey)}
              </p>
            </button>
          );
        })}
      </div>
    </Section>
  );
}

// ─── Simple mode section (GHE #476) ───
//
// A switch, not the three-card grid Appearance and Language use: those pick one
// of several values, this is on or off, and borrowing their shape would imply a
// choice that isn't there.
//
// Deliberately describes the OUTCOME ("see only what you use") rather than the
// mechanism ("hides the space tree"). The people this exists for are the ones
// who do not know what a space tree is — naming internals here would make the
// setting unreadable to exactly its audience.

function SimpleModeSection(): React.ReactElement {
  const { t } = useTranslation(['settings', 'common']);
  const { user, setUser } = useAuth();

  const enabled = user?.simpleMode ?? false;

  const mutation = useMutation({
    mutationFn: (next: boolean) => meApi.updateProfile({ simpleMode: next }),
    // The PATCH echoes the fresh AuthenticatedUser, exactly as LanguageSection
    // above — keep the auth context in sync so the sidebar re-renders at once.
    onSuccess: (response) => setUser(response.data.user),
    onError: () => toast.error(t('common:state.error')),
  });

  return (
    <Section title={t('simpleMode.title')} description={t('simpleMode.description')}>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={mutation.isPending}
        onClick={() => mutation.mutate(!enabled)}
        className={`flex w-full items-center gap-4 rounded-lg border p-4 text-left transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] disabled:opacity-60 ${
          enabled
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
            : 'border-border bg-surface-raised hover:border-border-strong hover:bg-surface-overlay'
        }`}
      >
        <div className="flex-1">
          <p
            className={`text-sm font-medium ${
              enabled ? 'text-primary-700' : 'text-[var(--text-primary)]'
            }`}
          >
            {t('simpleMode.label')}
          </p>
          <p className="mt-0.5 text-xs text-[var(--text-secondary)]">{t('simpleMode.hint')}</p>
        </div>
        {/* The track. `aria-hidden` because the button itself carries the
            switch role and state — a screen reader should hear one control. */}
        <span
          aria-hidden="true"
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150 ${
            enabled ? 'bg-primary-600' : 'bg-border-strong'
          }`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-[left] duration-150 ${
              enabled ? 'left-[22px]' : 'left-0.5'
            }`}
          />
        </span>
      </button>

      {/* The checklist appears WITH the switch, not on a page of its own: the
          people this exists for will not go hunting for a second setting, and a
          choice offered at the moment you turn something on is one they can
          actually make. Collapsed away entirely when off — it would be noise. */}
      {enabled && <SurfaceChecklist />}
    </Section>
  );
}

// ─── Simple-mode surface checklist (GHE #476) ───
//
// Lists every destination this person MAY see, pre-ticked from the registry's
// own defaults. Two properties matter more than the markup:
//
//   1. It can only SUBTRACT. The list is `visibleNavDestinations(user)` — the
//      role-gated set — so a surface someone has no access to is not offered at
//      all. Ticking can never grant.
//   2. Absence is a DEFAULT, not an "off". Nothing is written until the person
//      actually changes something, and only the changed key is sent, so a
//      surface added to the app later still arrives at its own default rather
//      than inheriting a stale snapshot.

function SurfaceChecklist(): React.ReactElement {
  const { t } = useTranslation(['settings', 'common']);
  const { user, setUser } = useAuth();

  const destinations = visibleNavDestinations(user);

  const mutation = useMutation({
    // Typed off the Zod INPUT, not the read type. The two differ on purpose:
    // reads keep `surfaceKey: string` because a stored row may hold a key that
    // has since been retired, while a write must be one of TODAY's keys or the
    // API rejects it. Taking the write type from the schema keeps them honest.
    mutationFn: (pref: UpdateSurfacePreferencesInput['preferences'][number]) =>
      meApi.updateSurfacePreferences({ preferences: [pref] }),
    onSuccess: (response) => {
      // The endpoint returns the caller's FULL override set; merge it into the
      // auth context so the sidebar re-renders from one source of truth rather
      // than a locally-patched copy that could drift from the server's.
      if (user) setUser({ ...user, surfacePreferences: response.data });
    },
    onError: () => toast.error(t('common:state.error')),
  });

  return (
    <fieldset className="mt-4 border-t border-border pt-4" disabled={mutation.isPending}>
      <legend className="sr-only">{t('simpleMode.surfacesLegend')}</legend>
      <p className="mb-2 text-xs text-[var(--text-secondary)]">{t('simpleMode.surfacesHint')}</p>
      <div className="flex flex-col gap-1">
        {destinations.map((destination) => {
          const checked = isEnabledInSimpleMode(destination, user);
          return (
            <label
              key={destination.key}
              className="coarse:min-h-[44px] flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-[var(--text-primary)] hover:bg-surface-overlay"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => mutation.mutate({ surfaceKey: destination.key, enabled: !checked })}
                className="h-4 w-4 rounded border-border-strong text-primary-600 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
              />
              <destination.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t(destination.labelKey, { ns: 'common' })}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

// ─── Profile section ───

function ProfileSection(): React.ReactElement {
  const { t } = useTranslation('settings');
  const { user } = useAuth();

  if (!user) {
    return (
      <Section title={t('profile.title')} description={t('profile.description')}>
        <div className="rounded-lg border border-border bg-surface-raised p-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="skeleton h-12 w-12 rounded-full" />
              <div className="flex flex-col gap-1.5">
                <div className="skeleton h-4 w-32 rounded" />
                <div className="skeleton h-3 w-48 rounded" />
              </div>
            </div>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <Section title={t('profile.title')} description={t('profile.description')}>
      <div className="rounded-lg border border-border bg-surface-raised p-4">
        <div className="flex items-center gap-4 mb-4">
          <PersonCircle
            userId={user.id}
            displayName={user.displayName}
            sizeClass="h-12 w-12"
            textClass="text-base"
          />
          <div className="min-w-0">
            <p className="text-base font-semibold text-[var(--text-primary)] truncate">
              {user.displayName}
            </p>
            <p className="text-sm text-[var(--text-secondary)] truncate">{user.email}</p>
          </div>
        </div>
        <dl className="border-t border-border pt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ProfileField
            label={t('profile.access.label')}
            value={user.isSuperAdmin ? t('profile.access.superadmin') : t('profile.access.scoped')}
          />
          <ProfileField label={t('profile.userId.label')} value={user.id} mono />
        </dl>
      </div>
      <p className="mt-2 text-xs text-[var(--text-tertiary)]">{t('profile.note')}</p>
    </Section>
  );
}

function ProfileField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): React.ReactElement {
  return (
    <div>
      <dt className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide mb-0.5">
        {label}
      </dt>
      <dd
        className={`text-sm text-[var(--text-primary)] truncate ${mono ? 'font-mono text-xs' : ''}`}
      >
        {value}
      </dd>
    </div>
  );
}

// ─── About section ───

function AboutSection(): React.ReactElement {
  const { t } = useTranslation('settings');
  return (
    <Section title={t('about.title')} description={t('about.description')}>
      <div className="rounded-lg border border-border bg-surface-raised p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-50">
            <svg
              className="w-5 h-5 text-primary-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <div>
            <p className="text-base font-semibold text-[var(--text-primary)]">{APP_NAME}</p>
            <p className="text-sm text-[var(--text-secondary)]">{t('about.tagline')}</p>
          </div>
        </div>
        <dl className="border-t border-border pt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <AboutField label={t('about.version.label')} value="0.1.0" />
          <AboutField label={t('about.environment.label')} value={t('about.environment.value')} />
          <AboutField label={t('about.replaces.label')} value={t('about.replaces.value')} />
          <AboutField label={t('about.support.label')} value={t('about.support.value')} />
        </dl>
      </div>
    </Section>
  );
}

function AboutField({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <dt className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide mb-0.5">
        {label}
      </dt>
      <dd className="text-sm text-[var(--text-primary)]">{value}</dd>
    </div>
  );
}

// ─── Notifications tab content ───
// Personal email opt-outs (Phase 9 Batch C round 4, ADR 0022 D4) — visible to ALL
// authenticated users, like Appearance/Language above (not gated like Clients/Alerts).

function NotificationsTabContent(): React.ReactElement {
  const { t } = useTranslation('settings');
  return (
    <Section title={t('notificationsTab.title')} description={t('notificationsTab.description')}>
      <NotificationsTab />
    </Section>
  );
}

// ─── Access tab content ───
// Unified access console — visible to any user (My Access for all;
// People/Workspaces/Spaces/Requests/Activity gated inside the console).

function AccessTabContent(): React.ReactElement {
  const { t } = useTranslation('settings');
  const { user } = useAuth();

  // ADR 0035 D7 — the personal half of the Access split: what can I reach,
  // request access. The management console (Workspaces/Spaces/People/
  // Activity) lives on /app/admin now; this tab renders MyAccessView
  // directly, for EVERY member, with no console chrome around it.
  const content = (
    <Section title={t('myAccessTab.title')} description={t('myAccessTab.description')}>
      {user ? <MyAccessView user={user} /> : null}
    </Section>
  );

  // #64's width finding, simplified by the D7 split: this tab is ALWAYS the
  // MyAccessView card list now (the wide-table console lives on /app/admin),
  // so everyone gets the reading-width cap — full-width stretched it to
  // ~1392px of sparse cards.
  return <NarrowTab>{content}</NarrowTab>;
}

// ─── Companies tab content ───
// Superadmin only (ADR 0016). Manages the top-level Company entities that
// own workspaces and clients.

// ─── Requests tab content ───
// Unified inbox — access requests (existing approver logic, unchanged) plus
// client requests (superadmin-only section). Replaces the Access console's
// former "Requests" sub-tab (ADR 0013 D4).

function RequestsTabContent(): React.ReactElement {
  const { t } = useTranslation('settings');
  return (
    <Section title={t('requestsTab.title')} description={t('requestsTab.description')}>
      <RequestsHub />
    </Section>
  );
}

// Companies / Calendars / Knowledge-builder / Admin / Deleted (phase 1) and
// Clients / Task-types / Alerts (phase 2) tab contents moved to
// routes/app/admin.tsx (ADR 0035).

// ─── Sidebar nav ───

interface SettingsSidebarProps {
  activeTab: SettingsTab;
  onTabChange: (tab: SettingsTab) => void;
}

function SettingsSidebar({ activeTab, onTabChange }: SettingsSidebarProps): React.ReactElement {
  const { t } = useTranslation('settings');

  return (
    <nav aria-label={t('nav.ariaLabel')} className="flex flex-col gap-1">
      {TABS.map(({ id, labelKey, icon: Icon }) => {
        const isActive = activeTab === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onTabChange(id)}
            aria-current={isActive ? 'page' : undefined}
            className={`flex items-center gap-3 h-10 w-full rounded-lg px-3 text-sm font-medium text-left transition-colors duration-150 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
              isActive
                ? 'bg-surface-sidebar-active text-primary-700'
                : 'text-[var(--text-secondary)] hover:bg-surface-overlay hover:text-[var(--text-primary)]'
            }`}
          >
            <Icon
              className={`h-4 w-4 flex-shrink-0 ${
                isActive ? 'text-primary-500' : 'text-[var(--text-tertiary)]'
              }`}
              aria-hidden="true"
            />
            <span className="flex-1">{t(labelKey)}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ─── Mobile top tabs ───

interface MobileTabsProps {
  activeTab: SettingsTab;
  onTabChange: (tab: SettingsTab) => void;
}

function MobileTabs({ activeTab, onTabChange }: MobileTabsProps): React.ReactElement {
  const { t } = useTranslation('settings');

  return (
    <div
      role="tablist"
      aria-label={t('nav.ariaLabel')}
      className="flex border-b border-border overflow-x-auto"
    >
      {TABS.map(({ id, labelKey, icon: Icon }) => {
        const isActive = activeTab === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onTabChange(id)}
            className={`coarse:min-h-[44px] flex items-center gap-2 h-10 px-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors duration-150 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
              isActive
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-border-strong'
            }`}
          >
            <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {t(labelKey)}
          </button>
        );
      })}
    </div>
  );
}

// ─── Tab content router ───

// Tabs whose content is short, card/form-based settings (Appearance,
// Language, About, Profile, personal notification toggles) rather than an
// admin console with wide tables — these get an inner reading-width cap so
// they don't stretch absurdly now that the page itself is full width (#64).
// The admin-console tabs (Clients, Alerts, Requests, Companies, Calendars,
// Admin) render list/table rows that scale fine with more room and are left
// unbounded on purpose (Empresas/Calendarios all carry wide tables that
// benefit). Access is a split case, handled inside AccessTabContent itself
// rather than here: it's full-width for managers/superadmins (their
// Workspaces/Spaces sub-views have wide tables too), but non-managers only
// ever see the My Access card list (#63 hides the admin sub-views for them)
// — plain card content, same reading-width cap as this exemption list.
function NarrowTab({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="max-w-3xl">{children}</div>;
}

function TabContent({ tab }: { tab: SettingsTab }): React.ReactElement | null {
  switch (tab) {
    case 'general':
      return (
        <NarrowTab>
          <div className="flex flex-col gap-8">
            <AppearanceSection />
            <TransparencySection />
            <LanguageSection />
            <SimpleModeSection />
            <div className="border-t border-border" role="separator" />
            <AboutSection />
          </div>
        </NarrowTab>
      );
    case 'profile':
      return (
        <NarrowTab>
          <ProfileSection />
        </NarrowTab>
      );
    case 'my-knowledge':
      // docs/design/components/conocimientos-mis-conocimientos.md §1.2:
      // MyKnowledgeTab renders its OWN internal heading (list title/
      // description, or nothing once drilled into the fill form) — not
      // wrapped in <Section title=... /> like Notifications below, since a
      // fixed page-level heading here would stay visible above the fill
      // form after drilling in (redundant chrome once the form has its own
      // title). <NarrowTab> matches Profile/Notifications' reading width —
      // this is a vertically-stacked card list + form, not a wide table.
      return (
        <NarrowTab>
          <MyKnowledgeTab />
        </NarrowTab>
      );
    case 'notifications':
      return (
        <NarrowTab>
          <NotificationsTabContent />
        </NarrowTab>
      );
    case 'access':
      return <AccessTabContent />;
    case 'requests':
      return <RequestsTabContent />;
    default:
      // The five admin ids are handled by SettingsPage's redirect before
      // TabContent ever renders (ADR 0035 phase 1).
      return null;
  }
}

// ─── Page ───

/** The tab ids that moved to the /app/admin console (ADR 0035 phase 1). Kept
 * in `settingsSearchSchema` so an old link — including the ones in
 * already-sent emails, which never expire — redirects instead of silently
 * losing its destination. Exported for the redirect test. */
export const TABS_MOVED_TO_ADMIN = [
  'companies',
  'calendars',
  'knowledge',
  'admin',
  'deleted',
  'clients',
  'task-types',
  'alerts',
  'dev-machines',
] as const;

type MovedTab = (typeof TABS_MOVED_TO_ADMIN)[number];

function isMovedTab(tab: SettingsTab | undefined): tab is MovedTab {
  return tab !== undefined && (TABS_MOVED_TO_ADMIN as readonly string[]).includes(tab);
}

function SettingsPage(): React.ReactElement {
  const { t } = useTranslation('settings');
  const {
    tab: tabParam,
    machineId,
    questionnaireStatus,
    clientSearch,
  } = useSearch({ from: '/app/settings' });
  const navigate = useNavigate({ from: '/app/settings' });

  // ─── Permanent redirect for moved admin tabs (ADR 0035 D10) ───
  // BEFORE any gating: the console's own guard decides what an unauthorized
  // visitor sees (it bounces them back here WITHOUT the tab param — no loop).
  // `questionnaireStatus` (old builder links), `clientSearch` (old client
  // links) and `machineId` (old machine-detail links) ride along; the admin
  // schema drops whichever does not apply.
  if (isMovedTab(tabParam)) {
    return (
      <Navigate
        to="/app/admin"
        search={{ tab: tabParam, questionnaireStatus, clientSearch, machineId }}
        replace
      />
    );
  }

  // GHE #284 item 2: the URL is now the source of truth — previously this was
  // a `useState` seeded ONCE from `?tab=` and never written back, so switching
  // tabs left the URL lying about where you were (unshareable, and a reload
  // jumped you back to whatever the URL still said). `tab` is read directly
  // from the URL every render; `handleTabChange` below `navigate()`s instead
  // of setting local state.
  // Every remaining tab is personal and ungated (ADR 0035 phase 4 retired the
  // role plumbing) — the moved-tab redirect above owns every role-gated id.
  const safeTab: SettingsTab = tabParam ?? 'general';

  const handleTabChange = (tab: SettingsTab): void => {
    // `replace: true` — tab switching is a lateral move within the same page,
    // not a navigation worth stepping back through one entry at a time.
    void navigate({
      search: (prev) => ({ ...prev, tab }),
      replace: true,
    });
  };

  return (
    <div className="min-h-full px-4 py-8 sm:px-6 lg:px-8">
      {/* Full-width container kept for parity with sibling pages (#64) even
       * though every remaining tab is card/form-shaped — each applies its own
       * inner max-w-3xl via <NarrowTab> so content stays readable. */}
      <div className="mx-auto w-full max-w-[1600px]">
        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-raised border border-border">
              <Settings className="h-5 w-5 text-[var(--text-secondary)]" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{t('page.title')}</h1>
          </div>
          <p className="text-sm text-[var(--text-secondary)] ml-12">{t('page.description')}</p>
        </div>

        {/* Mobile: top tabs (visible below lg) */}
        <div className="lg:hidden mb-6">
          <MobileTabs activeTab={safeTab} onTabChange={handleTabChange} />
        </div>

        {/* Desktop: sidebar + content layout */}
        <div className="flex gap-8">
          {/* Sidebar (desktop only) */}
          <aside className="hidden lg:block w-44 flex-shrink-0">
            <SettingsSidebar activeTab={safeTab} onTabChange={handleTabChange} />
          </aside>

          {/* Content area — a plain div, not a <main>: AppShell already renders
              the page's one and only <main> landmark around the Outlet (#393). */}
          <div className="flex-1 min-w-0">
            <TabContent tab={safeTab} />
          </div>
        </div>
      </div>
    </div>
  );
}
